import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import {
  addCompanyInformation,
  addStakeholder,
  deleteStakeholder,
  getBuildingDetails,
  getKYCBusinessTypes,
  getStakeholders,
  getTenantCompany,
  getListMFAEnabled,
  getPaymentOptions,
  getTenantDetails,
  updateStakeholder,
} from '../../auth/_requests';
import { useModal } from '../../helpers/hooks/modalContext';
import { ensurePayPrerequisitesMet } from '../../helpers/utils/payFlowGuard';
import {
  buildingDetails,
  PaymentOption,
  Stakeholder,
  tenantDetails,
} from '../../helpers/utils/types';
import { showToast } from '../../helpers/utils/showToast';
import { Spinner } from '../../helpers/UI/Spinner/Spinner';
import ProfileHeaderCard from './components/ProfileHeaderCard';
import PersonalInfoCard from './components/PersonalInfoCard';
import PaymentMethodsCard from './components/PaymentMethodsCard';
import SecurityCard from './components/SecurityCard';
import BusinessDetailsCard from './components/BusinessDetailsCard';
import HelpSupportCard from './components/HelpSupportCard';
import SignOutCard from './components/SignOutCard';
import DeleteAccountButton from './components/DeleteAccountButton';
import { BusinessFormState, ProfileFormState, StakeholderFormState } from './profileTypes';
import { formatDate } from './profileUtils';

const ProfilePage = () => {
  const { auth, currentUser, logout } = useAuth();
  const { setModal, modalState } = useModal();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [savingStakeholder, setSavingStakeholder] = useState(false);
  const [tenantData, setTenantData] = useState<tenantDetails | null>(null);
  const [buildings, setBuildings] = useState<buildingDetails[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentOption[]>([]);
  const [enabledMfaCount, setEnabledMfaCount] = useState(0);
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [kycBusinessTypes, setKycBusinessTypes] = useState<
    { kycBusinessTypeId: string; value: string }[]
  >([]);
  const [editingStakeholderId, setEditingStakeholderId] = useState<string | null>(null);
  const [form, setForm] = useState<ProfileFormState>({
    firstName: '',
    lastName: '',
    middleName: '',
    occupation: '',
    dateOfBirth: '',
  });
  const [businessForm, setBusinessForm] = useState<BusinessFormState>({
    name: '',
    displayName: '',
    kycBusinessTypeId: '',
  });
  const [stakeholderForm, setStakeholderForm] = useState<StakeholderFormState>({
    firstName: '',
    lastName: '',
    email: '',
  });
  const isKybEnabled = Boolean(tenantData?.tenant?.tenantCompanyId);

  const fullName = useMemo(() => {
    const names = [form.firstName, form.lastName].filter(Boolean);
    return names.length ? names.join(' ') : currentUser?.user?.fullname || 'Tenant';
  }, [currentUser?.user?.fullname, form.firstName, form.lastName]);

  const initials = useMemo(() => {
    const first = form.firstName?.[0] || currentUser?.user?.firstName?.[0] || 'T';
    const last = form.lastName?.[0] || currentUser?.user?.lastName?.[0] || 'P';
    return `${first}${last}`.toUpperCase();
  }, [currentUser?.user?.firstName, currentUser?.user?.lastName, form.firstName, form.lastName]);

  const fetchAll = useCallback(async () => {
    if (!auth?.data?.token) return;
    const token = auth.data.token;
    setLoading(true);
    try {
      const [tenantResponse, paymentResponse, mfaResponse, buildingsRes] = await Promise.all([
        getTenantDetails(token),
        getPaymentOptions(token),
        getListMFAEnabled(token),
        getBuildingDetails(token),
      ]);

      setTenantData(tenantResponse.data);
      setBuildings(buildingsRes.data || []);
      setPaymentMethods(paymentResponse);
      setEnabledMfaCount(mfaResponse?.data?.filter((item) => item.isEnabled).length || 0);
      setForm({
        firstName: tenantResponse.data.user?.firstName || '',
        lastName: tenantResponse.data.user?.lastName || '',
        middleName: tenantResponse.data.user?.middleName || '',
        occupation: tenantResponse.data.tenant?.occupation || '',
        dateOfBirth: formatDate(tenantResponse.data.user?.dateOfBirth),
      });

      const [typesResponse, companyResponse] = await Promise.all([
        getKYCBusinessTypes(token),
        tenantResponse.data.tenant?.tenantCompanyId
          ? getTenantCompany(token)
          : Promise.resolve(null),
      ]);

      setKycBusinessTypes(typesResponse);
      if (companyResponse?.data) {
        setBusinessForm({
          name: companyResponse.data.name || '',
          displayName: companyResponse.data.displayName || '',
          kycBusinessTypeId: companyResponse.data.kycBusinessTypeId || '',
        });
        const stakeholdersResponse = await getStakeholders(token);
        setStakeholders(stakeholdersResponse.data || []);
      } else {
        setStakeholders([]);
      }
    } catch (error) {
      showToast({
        message: `Error loading profile: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [auth?.data?.token]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (modalState?.refreshProfile || modalState?.refreshProperties) {
      void fetchAll();
    }
  }, [modalState?.refreshProfile, modalState?.refreshProperties, fetchAll]);

  const handleAddPaymentMethod = async () => {
    if (!auth?.data?.token) return;
    const tenantBuildingId = buildings[0]?.building.tenantBuildingId;
    if (!tenantBuildingId) {
      showToast({
        message: 'Add a property before adding a payment method.',
        type: 'error',
      });
      return;
    }
    const isPayPrerequisitesMet = await ensurePayPrerequisitesMet(
      tenantBuildingId,
      buildings,
      setModal
    );
    if (isPayPrerequisitesMet) {
      setModal('add-payment-method', { tenantBuildingId });
    }
  };

  const handleSaveBusiness = async () => {
    if (!auth?.data?.token) return;
    if (!businessForm.name || !businessForm.kycBusinessTypeId) {
      showToast({ message: 'Company legal name and business type are required', type: 'error' });
      return;
    }
    setSavingBusiness(true);
    try {
      await addCompanyInformation(auth.data.token, {
        name: businessForm.name,
        displayName: businessForm.displayName || undefined,
        kycBusinessTypeId: businessForm.kycBusinessTypeId,
      });
      showToast({ message: 'Business details saved successfully', type: 'success' });
      await fetchAll();
    } catch (error) {
      showToast({
        message: `Error saving business details: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error',
      });
    } finally {
      setSavingBusiness(false);
    }
  };

  const handleSaveStakeholder = async () => {
    if (!auth?.data?.token) return;
    if (!stakeholderForm.firstName || !stakeholderForm.lastName || !stakeholderForm.email) {
      showToast({
        message: 'Stakeholder first name, last name and email are required',
        type: 'error',
      });
      return;
    }
    setSavingStakeholder(true);
    try {
      if (editingStakeholderId) {
        await updateStakeholder(auth.data.token, editingStakeholderId, stakeholderForm);
        showToast({ message: 'Stakeholder updated successfully', type: 'success' });
      } else {
        await addStakeholder(auth.data.token, stakeholderForm);
        showToast({ message: 'Stakeholder added successfully', type: 'success' });
      }
      const stakeholdersResponse = await getStakeholders(auth.data.token);
      setStakeholders(stakeholdersResponse.data || []);
      setStakeholderForm({ firstName: '', lastName: '', email: '' });
      setEditingStakeholderId(null);
    } catch (error) {
      showToast({
        message: `Error saving stakeholder: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error',
      });
    } finally {
      setSavingStakeholder(false);
    }
  };

  const handleDeleteStakeholder = async (id: string) => {
    if (!auth?.data?.token) return;
    try {
      await deleteStakeholder(auth.data.token, id);
      const stakeholdersResponse = await getStakeholders(auth.data.token);
      setStakeholders(stakeholdersResponse.data || []);
      showToast({ message: 'Stakeholder deleted successfully', type: 'success' });
    } catch (error) {
      showToast({
        message: `Error deleting stakeholder: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error',
      });
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className='pg w-full' id='pg-profile'>
      <div className='hero'>
        <div className='pg-hero'>
          <div className='hero-beam' aria-hidden />
          <div className='hero-orb orb-blue' aria-hidden />
          <div className='orb-purple-p' aria-hidden />
          <div className='relative z-[1] px-5 py-6 sm:px-8 sm:py-7'>
            <div className='pg-hero-eye'>Account</div>
            <div className='pg-hero-title'>Profile & settings</div>
            <div className='pg-hero-sub'>
              Manage your personal information, security settings, and payment methods.
            </div>
          </div>
        </div>
      </div>

      <ProfileHeaderCard fullName={fullName} initials={initials} tenantData={tenantData} />

      <div className='two-col'>
        <div className='two-col-l'>
          <PersonalInfoCard form={form} />
          <SecurityCard
            tenantData={tenantData}
            enabledMfaCount={enabledMfaCount}
            onResetPassword={() => setModal('reset-password')}
            onManageMfa={() => setModal('mfa-settings')}
            onAddPhone={() => setModal('phone-settings', { phoneSettingsMode: 'add' })}
            onEditPhone={() => setModal('phone-settings', { phoneSettingsMode: 'edit' })}
            onVerifyPhone={() => setModal('phone-settings', { phoneSettingsMode: 'verify' })}
          />
          {isKybEnabled && (
            <BusinessDetailsCard
              businessForm={businessForm}
              stakeholders={stakeholders}
              stakeholderForm={stakeholderForm}
              kycBusinessTypes={kycBusinessTypes}
              editingStakeholderId={editingStakeholderId}
              savingBusiness={savingBusiness}
              savingStakeholder={savingStakeholder}
              onBusinessFormChange={setBusinessForm}
              onStakeholderFormChange={setStakeholderForm}
              onSaveBusiness={handleSaveBusiness}
              onSaveStakeholder={handleSaveStakeholder}
              onEditStakeholder={(stakeholder) => {
                setEditingStakeholderId(stakeholder.tenantStakeholderId);
                setStakeholderForm({
                  firstName: stakeholder.firstName,
                  lastName: stakeholder.lastName,
                  email: stakeholder.email,
                });
              }}
              onDeleteStakeholder={handleDeleteStakeholder}
            />
          )}
          <SignOutCard onLogout={logout} />
        </div>
        <div className='two-col-l'>
          <PaymentMethodsCard
            paymentMethods={paymentMethods}
            onAddPaymentMethod={() => void handleAddPaymentMethod()}
            onViewAll={() => navigate('/payment-methods')}
          />
          <HelpSupportCard onOpenPrivacyPolicy={() => setModal('privacy-policy')} />
          <DeleteAccountButton onDelete={() => setModal('delete-account')} />
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
